import { FC, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import type { PlaceItem, PlaceSearchContextForSave } from '../../../../../types/place.types';
import { useSavePlaceToIdeaMutation } from '../../../../../store/services/ideasApi';
import { useGetTripsQuery, useSavePlaceToTripMutation } from '../../../../../store/services/tripsApi';
import {
  buildPlaceToIdeaPayload,
  validatePlaceStopWithinTrip,
} from '../../../../../utils/placeToIdeaPayload';
import { getApiErrorMessage } from '../../../../../utils/apiErrorMessage';
import {
  PENDING_PLACE_TO_IDEA_STORAGE_KEY,
  PENDING_PLACE_TO_TRIP_STORAGE_KEY,
} from '../../../../../constants/discoverIdeaFlow';
import ChooseWhereToSaveModal, {
  type ChooseWhereToSavePayload,
} from '../ChooseWhereToSaveModal/ChooseWhereToSaveModal';
import TripStopScheduleModal from '../TripStopScheduleModal';
import { useDiscoverStoppableSaveFlow } from '../../hooks/useDiscoverStoppableSaveFlow';

export interface SavePlaceModalProps {
  open: boolean;
  onClose: () => void;
  place: PlaceItem | null;
  searchContext: PlaceSearchContextForSave;
  onSaved?: (message: string) => void;
}

const SavePlaceModal: FC<SavePlaceModalProps> = ({ open, onClose, place, searchContext, onSaved }) => {
  const navigate = useNavigate();
  const [savePlaceToIdea, { isLoading: isSavingToIdea }] = useSavePlaceToIdeaMutation();
  const [savePlaceToTrip, { isLoading: isSavingToTrip }] = useSavePlaceToTripMutation();
  const { data: trips = [] } = useGetTripsQuery({});
  const isSaving = isSavingToIdea || isSavingToTrip;

  const {
    saveError,
    setSaveError,
    chooseOpen,
    scheduleOpen,
    pendingTripSave,
    closeTripSchedule,
    openTripSchedule,
    beginTripSaveFromChoose,
    beginNewTripSave,
  } = useDiscoverStoppableSaveFlow(open);

  const selectedTrip = useMemo(() => {
    if (pendingTripSave?.tripId == null) return null;
    return trips.find(t => t.id === pendingTripSave.tripId) ?? null;
  }, [trips, pendingTripSave?.tripId]);

  const tripScheduleBounds = useMemo(() => {
    if (!selectedTrip?.start_date || !selectedTrip?.end_date) {
      return { minDate: undefined, maxDate: undefined };
    }
    return {
      minDate: selectedTrip.start_date.trim().slice(0, 10),
      maxDate: selectedTrip.end_date.trim().slice(0, 10),
    };
  }, [selectedTrip]);

  useEffect(() => {
    if (!open) {
      setSaveError(null);
    }
  }, [open, setSaveError]);

  const persistPendingIdeaBody = () => {
    if (!place) return false;
    const body = buildPlaceToIdeaPayload(place, searchContext);
    try {
      sessionStorage.setItem(PENDING_PLACE_TO_IDEA_STORAGE_KEY, JSON.stringify({ body }));
      return true;
    } catch {
      return false;
    }
  };

  const persistPendingTripBody = (times: { tripStopStartDateTime: string; tripStopEndDateTime: string }) => {
    if (!place) return false;
    const body = buildPlaceToIdeaPayload(place, searchContext, {
      start_date_time: times.tripStopStartDateTime,
      end_date_time: times.tripStopEndDateTime,
    });
    try {
      sessionStorage.setItem(PENDING_PLACE_TO_TRIP_STORAGE_KEY, JSON.stringify({ body }));
      return true;
    } catch {
      return false;
    }
  };

  const handleCreateNewIdeaList = () => {
    if (!persistPendingIdeaBody()) return;
    onClose();
    navigate('/ideas?fromDiscover=1');
  };

  const handleCreateNewTripStub = () => {
    beginNewTripSave();
  };

  const handleTripSelect = (tripId: number) => {
    openTripSchedule({ tripId, isNewTrip: false });
  };

  const handleChooseSave = async (payload: ChooseWhereToSavePayload) => {
    setSaveError(null);
    if (!place) return;

    if (payload.tab === 'ideas') {
      if (payload.ideaId == null) {
        setSaveError('Select an ideas list to save this place.');
        return;
      }
      const body = buildPlaceToIdeaPayload(place, searchContext);
      try {
        const res = await savePlaceToIdea({ ideaId: payload.ideaId, body }).unwrap();
        const msg = res.message || `Saved to ${res.idea_list_name || 'your list'}.`;
        onSaved?.(msg);
        onClose();
      } catch (e: unknown) {
        setSaveError(getApiErrorMessage(e, 'Could not save place. Try again.', { firstOnly: true }));
      }
      return;
    }

    beginTripSaveFromChoose(payload);
  };

  const savePlaceToExistingTrip = async (
    tripId: number,
    times: { tripStopStartDateTime: string; tripStopEndDateTime: string }
  ) => {
    const trip = trips.find(t => t.id === tripId);
    if (!trip?.start_date || !trip?.end_date) {
      setSaveError('This trip has no dates. Add trip dates before saving a stop.');
      return;
    }

    const rangeError = validatePlaceStopWithinTrip(
      trip.start_date,
      trip.end_date,
      times.tripStopStartDateTime,
      times.tripStopEndDateTime
    );
    if (rangeError) {
      setSaveError(rangeError);
      return;
    }

    if (!place) return;

    const body = buildPlaceToIdeaPayload(place, searchContext, {
      start_date_time: times.tripStopStartDateTime,
      end_date_time: times.tripStopEndDateTime,
    });

    try {
      const res = await savePlaceToTrip({ tripId, body }).unwrap();
      const msg = res.message || `Saved to ${res.trip_name || 'your trip'}.`;
      onSaved?.(msg);
      onClose();
    } catch (e: unknown) {
      setSaveError(
        getApiErrorMessage(e, 'Could not save place to this trip. Try again.', { firstOnly: true })
      );
    }
  };

  const handleScheduleCreate = async (times: {
    tripStopStartDateTime: string;
    tripStopEndDateTime: string;
  }) => {
    if (!pendingTripSave || !place) return;

    if (pendingTripSave.isNewTrip) {
      if (!persistPendingTripBody(times)) {
        setSaveError('Could not prepare this place for a new trip. Try again.');
        return;
      }
      onClose();
      navigate('/trips?fromDiscover=1');
      return;
    }

    if (pendingTripSave.tripId == null) {
      setSaveError('Select a trip to save this place.');
      return;
    }

    setSaveError(null);
    await savePlaceToExistingTrip(pendingTripSave.tripId, times);
  };

  return (
    <>
      <ChooseWhereToSaveModal
        open={chooseOpen}
        onClose={onClose}
        titleId="save-place-modal-title"
        searchInputName="save-place-search"
        onSave={handleChooseSave}
        isSaving={isSaving}
        disableFooterWhileSaving
        noIdeaListsEmptyMessage="You have no idea lists yet. Create one below."
        onCreateNewIdeaList={handleCreateNewIdeaList}
        onCreateNewTrip={handleCreateNewTripStub}
        onTripSelect={handleTripSelect}
        createNewIdeaListDisabled={!place}
        errorText={saveError}
        onErrorDismiss={() => setSaveError(null)}
      />

      <TripStopScheduleModal
        open={scheduleOpen}
        onClose={closeTripSchedule}
        onCreate={handleScheduleCreate}
        initialValues={null}
        titleId="save-place-stop-schedule-title"
        minDate={pendingTripSave?.isNewTrip ? undefined : tripScheduleBounds.minDate}
        maxDate={pendingTripSave?.isNewTrip ? undefined : tripScheduleBounds.maxDate}
        errorText={scheduleOpen ? saveError : null}
        onErrorDismiss={() => setSaveError(null)}
        isSubmitting={isSaving}
      />
    </>
  );
};

export default SavePlaceModal;
